import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { SearchPanel } from '@/components/search-panel';
import { ArticleResults } from '@/components/article-results';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { Button } from '@/components/ui/button';
import { useArticleSearch } from '@/lib/use-article-search';
import { useDeleteArticle } from '@/lib/queries';
import type { SearchResultItem } from '@shared/schemas';

const BASE = '/admin/articoli';

/**
 * Gestione articoli: stesso layout della home (pannello di ricerca sticky +
 * indice per data / risultati con ArticleCard), con in più le azioni admin —
 * "Nuovo articolo" e, su ogni risultato, modifica ed elimina.
 */
export function AdminArticlesPage() {
  const articleSearch = useArticleSearch();
  const del = useDeleteArticle();
  const [pendingDelete, setPendingDelete] = useState<{ id: string; titolo: string } | null>(null);

  const renderActions = (item: SearchResultItem) => (
    <>
      <Button asChild variant="ghost" size="icon" aria-label="Modifica">
        <Link to={`${BASE}/${item.id}`}>
          <Pencil className="h-4 w-4" />
        </Link>
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Elimina"
        onClick={() => setPendingDelete({ id: item.id, titolo: item.titolo })}
      >
        <Trash2 className="h-4 w-4 text-(--color-destructive)" />
      </Button>
    </>
  );

  return (
    <div className="container-wide pb-6 sm:pb-8">
      <div className="md:sticky md:top-14 z-30 -mx-4 px-4 bg-(--color-brand-sage) py-4 mb-4 border-b border-(--color-border)">
        <SearchPanel />
      </div>

      <div className="flex items-center justify-end mb-4">
        <Button asChild size="sm">
          <Link to={`${BASE}/nuovo`}>
            <Plus className="h-4 w-4" /> Nuovo articolo
          </Link>
        </Button>
      </div>

      <ArticleResults {...articleSearch} basePath={BASE} renderActions={renderActions} />

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        itemLabel={pendingDelete ? `l'articolo "${pendingDelete.titolo}"` : ''}
        isPending={del.isPending}
        onConfirm={async () => {
          if (pendingDelete) await del.mutateAsync(pendingDelete.id);
        }}
      />
    </div>
  );
}
